import PortalLayout from '@/components/layout/portal/PortalLayout';
import { useAppSelector } from '@/state/hooks';
import { selectCredentials } from '@/state/reducers/authSlice';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';

import PortalInstrumentsTable from '@/components/portal-modules/PortalInstrumentsTable';
import { convertToInstrumentRow, InstrumentRow } from '@/helpers/portalInstrumentsHelper';
import { Instrument, InstrumentResponse } from '@/types/sso';
import { TableFilter } from '@/types/table';
import Unauthorized401 from '@/Unauthorized401';
import useLanguage from '@/utils/hooks/useLanguage';
import usePortalAuthentication from '@/utils/hooks/usePortalAuthentication';
import { useScreenSizes } from '@/utils/providers/ScreenSizesProvider';
import { useSso } from '@/utils/providers/SSOProvider';
import { useDidUpdate } from '@mantine/hooks';
import { useDateFormatter, useNumberFormatter } from 'react-aria';
import Skeleton from 'react-loading-skeleton';

const cardHeight = 122;
const tableHeight = 204.2;

export type InstrumentTypeFilter =
    | 'N/A'
    | 'Crypto CFD'
    | 'Equity Index CFD'
    | 'Commodity CFD'
    | 'Rolling Fx'
    | 'Fx Spot'
    | 'Crypto Spot'
    | 'Futures CFD';

export interface InstrumentFilters {
    instrumentType: TableFilter<InstrumentTypeFilter>;
}

function PortalInstruments() {
    usePortalAuthentication();

    const lang = useLanguage();
    const screen = useScreenSizes();

    const credentials = useAppSelector(selectCredentials);

    const {
        instrumentsIncludeRatioApi,
        entityType,
        foundEntity,
        flattenEntities,
        instrumentCurrenciesApi,
        hasPermission
    } = useSso();

    const defaultFilters = useMemo(
        () => ({ instrumentType: { value: '' as any, label: lang.blotters.instrumentType } }),
        [lang]
    );

    const { isMarginClient, isDeliverableClient, isExecutingBroker } = entityType;

    const dateFormatter = useDateFormatter({ timeZone: 'UTC' });
    const numberFormatter = useNumberFormatter({ minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const [filters, setFilters] = useState<InstrumentFilters>(defaultFilters);
    const [isInitialMount, setIsInitialMount] = useState<boolean>(true);
    const [pagination, setPagination] = useState({ pageSize: 100 });

    const { instrumentType } = useMemo(() => filters, [filters]);

    const currentParentEntity = useMemo(
        () => flattenEntities?.find((entity) => entity.entityId === foundEntity?.parentId),
        [foundEntity, flattenEntities]
    );

    const legalEntityId = useMemo(() => {
        if (currentParentEntity) {
            const found = currentParentEntity.addresses?.find((address) => address.type === 'LEI')?.value;
            return found ? found : '-';
        } else {
            const found = foundEntity?.addresses?.find((address) => address.type === 'LEI')?.value;
            return found ? found : '-';
        }
    }, [foundEntity, currentParentEntity]);

    const { data: instruments } = instrumentsIncludeRatioApi;
    const { data: instrumentCurrencies } = instrumentCurrenciesApi;

    const instrumentData: InstrumentRow[] = useMemo(() => {
        if (instruments && instrumentCurrencies && foundEntity) {
            let returnValues: InstrumentResponse[] = [];
            returnValues = instruments.filter((instrument) => {
                const instrumentTypeVerified = isInstrumentType(instrument, instrumentType);

                // check if CFD on Future is expired
                const isExpired = isFutureCFDExpired(instrument);

                return instrumentTypeVerified && instrument.active && !isExpired;
            });
            returnValues.sort((a, b) => a.symbol?.localeCompare(b.symbol));

            return returnValues.map((instrument) =>
                convertToInstrumentRow(
                    instrument,
                    legalEntityId,
                    foundEntity.entityId,
                    instrumentCurrencies,
                    dateFormatter,
                    numberFormatter
                )
            );
        } else return [];
    }, [instruments, instrumentCurrencies, instrumentType, legalEntityId, foundEntity, dateFormatter, numberFormatter]);

    const resetInstrumentTypeFilter = useCallback(() => {
        if (isMarginClient)
            setFilters({ instrumentType: { label: lang.blotters.instrumentType, value: 'Crypto CFD' } });
        else if (isDeliverableClient)
            setFilters({ instrumentType: { label: lang.blotters.instrumentType, value: 'Fx Spot' } });
        else if (isExecutingBroker)
            setFilters({ instrumentType: { label: lang.blotters.instrumentType, value: 'Crypto CFD' } });
        else setFilters({ instrumentType: { label: lang.blotters.instrumentType, value: 'N/A' } });
    }, [isMarginClient, isDeliverableClient, isExecutingBroker, lang]);

    useEffect(() => resetInstrumentTypeFilter(), [entityType]);
    useDidUpdate(() => {
        setFilters({ instrumentType: { ...filters.instrumentType, label: lang.blotters.instrumentType } });
    }, [lang.blotters.instrumentType]);

    useEffect(() => {
        instrumentCurrenciesApi.mutate().then(() => {
            setIsInitialMount(false);
        });
    }, []);

    const isAuthorized = hasPermission('ACCOUNT_VIEW');

    return credentials ? (
        <PortalLayout isAuthorized={isAuthorized}>
            {isAuthorized && (
                <div className="flex flex-col w-full gap-2 sm:gap-4 p-2 sm:p-6 text-brand-text mt-16 lg:mt-0 max-h-full">
                    <div className="flex flex-wrap md:h-[34px] gap-2 w-full justify-between">
                        <div className="font-bold text-xl">{lang.navigation.instruments}</div>
                    </div>
                    {isInitialMount &&
                    (instrumentsIncludeRatioApi.isLoading || instrumentsIncludeRatioApi.isValidating) ? (
                        <Fragment>
                            {screen.mobile ? (
                                <div className="flex flex-col w-full gap-2 sm:gap-4">
                                    <Skeleton containerClassName="flex-1" height={cardHeight} />
                                    {/* <Skeleton containerClassName="flex-1" height={cardHeight} />
                                        <Skeleton containerClassName="flex-1" height={cardHeight} /> */}
                                </div>
                            ) : (
                                <div className="flex flex-wrap w-full gap-2 sm:gap-4">
                                    {/* <div className="flex flex-wrap w-full gap-2 sm:gap-4">
                                            <Skeleton containerClassName="flex-1" height={cardHeight} />
                                            <Skeleton containerClassName="flex-1" height={cardHeight} />
                                            <Skeleton containerClassName="flex-1" height={cardHeight} />
                                        </div> */}
                                    {/* <div className="flex w-full">
                                            <Skeleton containerClassName="flex-1" height={tableHeight} />
                                        </div> */}
                                    <div className="flex w-full">
                                        <Skeleton containerClassName="flex-1" height={tableHeight} />
                                    </div>
                                </div>
                            )}
                        </Fragment>
                    ) : (
                        <PortalInstrumentsTable
                            data={instrumentData}
                            filters={filters}
                            pagination={pagination}
                            setPagination={setPagination}
                            setFilters={setFilters}
                            defaultFilters={defaultFilters}
                            isLoading={instrumentsIncludeRatioApi.isLoading || instrumentsIncludeRatioApi.isValidating}
                            mutate={instrumentsIncludeRatioApi.mutate as any}
                        />
                    )}
                </div>
            )}
            {!isAuthorized && <Unauthorized401 />}
            {/* <ExportTradesModal opened={exportDisclosure[0]} handlers={exportDisclosure[1]} /> */}
        </PortalLayout>
    ) : null;
}

export default PortalInstruments;

const isInstrumentType = (instrument: InstrumentResponse, instrumentType: TableFilter<InstrumentTypeFilter>) => {
    switch (instrumentType.value) {
        case 'Futures CFD':
            return instrument.type === 'CFD' && instrument.subType === 'Future';
        case 'Crypto CFD':
            return instrument.type === 'CFD' && instrument.subType === 'Crypto';
        case 'Equity Index CFD':
            return instrument.type === 'CFD' && instrument.subType === 'EquityIndex';
        case 'Commodity CFD':
            return (instrument.type === 'CFD' && instrument.subType === 'Commodity') || instrument.subType === 'Energy';
        case 'Rolling Fx':
            return instrument.type === 'FX';
        case 'Fx Spot':
            return instrument.type === 'FX';
        case 'Crypto Spot':
            return instrument.type === 'Crypto';
        default:
            return false;
    }
};

export const isFutureCFDExpired = (instrument: Instrument): boolean => {
    // Only consider Future sub-type
    if (instrument.subType !== 'Future') return false;

    // If there's no end date, treat as not expired
    if (!instrument.endDate) return false;

    const expiry = new Date(instrument.endDate);

    // If the parsed expiry date is invalid, treat as not expired
    if (Number.isNaN(expiry.getTime())) return false;

    // Compare date-only (ignore time-of-day) by normalizing both to local midnight
    const today = new Date();

    today.setHours(0, 0, 0, 0);
    expiry.setHours(0, 0, 0, 0);

    return expiry < today;
};
